import React, { useRef, useState, useEffect } from "react";
import "./modal_data_goods_receive.css";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import { Button, Input, Modal, Space, Table } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import axios from "axios";

interface DataType {
    key: React.Key;
    receive_no: string;
    date: string;
    supplier: string;
    supplier_name: string;
    supplier_invoice_no: string;
    tax_invoice_no: string;
    warehouse: string;
    is_fully_returned?: boolean;
    draft_return_no?: string;
}

type DataIndex = keyof DataType;
interface ModalDataGoodsReceiveProps {
    onRowDoubleClick: (record: DataType) => void;
}

const ModalDataGoodsReceive: React.FC<ModalDataGoodsReceiveProps> = ({ onRowDoubleClick }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);
    const [data, setData] = useState<DataType[]>([]);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        const fetchData = async () => {
            if (loading === false) setLoading(true);
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-data`
                );
                if (response.data && Array.isArray(response.data)) {
                    setData(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch goods receive data:", error);
            } finally {
                setTimeout(() => setLoading(false), 300);
            }
        };

        fetchData();
    }, []);


    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps["confirm"],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.replace(/_/g, " ")}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}>
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}>
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}>
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}>
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
        onFilter: (value, record) =>
            (record[dataIndex] ?? "")
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: "Receive No",
            dataIndex: "receive_no",
            key: "receive_no",
            ...getColumnSearchProps("receive_no"),
            sorter: (a, b) => a.receive_no.localeCompare(b.receive_no),
            sortDirections: ["descend", "ascend"],
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            ...getColumnSearchProps("date"),
            sorter: (a, b) => a.date.localeCompare(b.date),
            sortDirections: ["descend", "ascend"],
        },
        {
            title: "Supplier",
            dataIndex: "supplier",
            key: "supplier",
            ...getColumnSearchProps("supplier"),
            sorter: (a, b) => a.supplier.localeCompare(b.supplier),
            sortDirections: ["descend", "ascend"],
        },
        {
            title: "Supplier Name",
            dataIndex: "supplier_name",
            key: "supplier_name",
            ...getColumnSearchProps("supplier_name"),
            sorter: (a, b) => a.supplier_name.localeCompare(b.supplier_name),
            sortDirections: ["descend", "ascend"],
        },
    ];

    return (
        <div id="iau9" style={transformStyle}>
            <div className="gjs-grid-row" id="iwh2">
                <div className="gjs-grid-column" id="io9i">
                    <Table<DataType>
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        rowClassName={(record) => (record.is_fully_returned ? "row-disabled-dimmed" : "")}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    if (record.is_fully_returned) return;
                                    if (record.draft_return_no) {
                                        Modal.warning({
                                            title: 'Perhatian',
                                            content: `Nomor ${record.receive_no} dengan nomor return ${record.draft_return_no} belum di selesaikan di data masih dalam status Draft !, Selesaikan terlebih dahulu.`,
                                        });
                                        return;
                                    }
                                    onRowDoubleClick(record);
                                },
                            };
                        }} />
                </div>
            </div>
        </div>
    );
};

export default ModalDataGoodsReceive;